import { Inject, Injectable } from '@angular/core';
import { AmenityItem, ApiGetInProgressApplicationsRS, ApiReadHotelContactInformationRS, ApiResponse, BtrReadProfileApplicationDetailResponse, BtrReadProfileResponse, CountryGroup, CountryGroupItem, RatePlan, ReadAddsOnListRQ, ReadAddsOnListRS, ReadAllApplicationForProfileRS, ReadApplicationConfigurationsRS, ReadApplicationPaymentSummaryRQ, ReadApplicationPaymentSummaryRS, ReadBTRAmenitiesSearchableRS, ReadBTRHotelRoomAmenitiesRS, ReadCountryHotelGroupListResponse, ReadProfileApplicationRQ, ReadRentalItemTypeRateAvailabilityRQ, ReadRentalItemTypeRateAvailabilityRS, ReadSearchDiscountByAccessCodeDistributionChannelCodeRQ, ReadSearchDiscountByAccessCodeDistributionChannelCodeRS, ReadSystemTypesByActivityTypeRQ, ReadSystemTypesByActivityTypeRS, RoomAmenity, RoomTypeRoomItem, RoomTypeRoomItemRoom, SaveDraftApplicationRQ, SaveDraftApplicationRS, SearchAvailbilityRQ, SearchAvailbilityRS, saveBookingInspectionRQ } from '../api/api.models';
import { Observable, of, shareReplay, Subject } from 'rxjs';
import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { APP_CONFIG } from '../config/app-config.service';
import { IAppConfig } from '../config/app-config.model';
import { LanguageService } from '../language.service';
import { SearchAvailabilityQuery, SearchAvailabilityQueryKeys } from '../models/booking-model';
import { date_valid, format_date, local_date, utc_date } from '../../utilities/date-utils';
import { XnStorageService } from '../xn-storage';
import { SelectedApplicationInfo, SelectedSiteTourInfo } from '../../data/interface';
import { IS_SKIP_HANDLE_ERROR } from '../api/api-http-context-token';
import { AuthService } from '../api/auth.service';
import { LogService } from '../log.service';
import { Router } from '@angular/router';
import { is_url } from '../../utils/utils';

const SELETED_RATE_PLAN = 'SELETED_RATE_PLAN';
const UTM_SOURCE_DATA = 'UTM-SOURCE-DATA';

@Injectable({
  providedIn: 'root'
})
export class BtrBookingService {

  constructor(
    @Inject(APP_CONFIG) protected config: IAppConfig,
    private http: HttpClient,
    private language: LanguageService,
    private storage: XnStorageService,
    private auth: AuthService,
    private log: LogService,
    private router: Router,
  ) { }

  private _readCountryHotelGroupListResponse: ReadCountryHotelGroupListResponse | undefined
  private _hotels: CountryGroupItem[] = [];
  private _hotelPhoneNumber: {propertyCode: string, phoneNumber: string}[] = []

  private $readCountryHotelGroupList: Observable<ReadCountryHotelGroupListResponse> | undefined;
  readCountryHotelGroupList(): Observable<ReadCountryHotelGroupListResponse> {
    if(!this.$readCountryHotelGroupList){
      this.$readCountryHotelGroupList = this._readCountryHotelGroupList().pipe(shareReplay());
    }

    return this.$readCountryHotelGroupList;
  }

  private _isTurnOnExternalSiteTour: boolean = false;
  isTurnOnExternalSiteTour(): boolean{
    return this._isTurnOnExternalSiteTour;
  }

  private _currentProperty: CountryGroupItem | undefined;
  private $_currentPropertySubject = new Subject<CountryGroupItem>();

  updateCurrentProperty(property: CountryGroupItem){
    this._currentProperty = property;
    this.$_currentPropertySubject.next(property);
  }

  getCurrentProperty(): CountryGroupItem | undefined{
    return this._currentProperty;
  }

  onPropertyChanged(): Observable<CountryGroupItem> {
    return this.$_currentPropertySubject.asObservable();
  }

  private _readCountryHotelGroupList(): Observable<ReadCountryHotelGroupListResponse> {
    if(this._readCountryHotelGroupListResponse){
      return of(this._readCountryHotelGroupListResponse);
    }

    return new Observable((observable) => {
      this.http.post<ReadCountryHotelGroupListResponse>('WBEBooking/ReadCountryHotelGroupList', {
        distributionChannelCode: this.config.apiServer.distributionChannelCode,
        cultureCode: this.getCurrentCultureCode(),
        domainCode: this.config.apiServer.domainCode,
        platformCode: this.config.apiServer.platformCode
      }).subscribe((res) => {
        this._readCountryHotelGroupListResponse = res;
        this._hotels = [];
        res.countryGroups?.forEach(e => {
          e.items?.forEach(f => {
            f.isOpenEndedContract = f.isOpenEndedContract && f.totalMonthsOpenEndedContract > 0;
            f.totalMonthsOpenEndedContract = f.totalMonthsOpenEndedContract;
            f.countryCode = e.countryCode;
            if(is_url(f.bookSiteTourURL)){
              this._isTurnOnExternalSiteTour = true;
            }
            this._hotels.push(f);
          })
        }),
        this._hotelPhoneNumber = [...res.hotelPhones];
        if(this._isTurnOnExternalSiteTour){
          document.querySelector('body').classList.add('use-external-sitetour');
        }
        observable.next(res)
      });
    })
  }

  getCurrentCultureCode(){
    return this.language.getCurrentCultureCode();
  }

  getHotelPhones() {
    return this._hotelPhoneNumber;
  }

  searchAvailbility(body: SearchAvailbilityRQ): Observable<SearchAvailbilityRS>{
    body.distributionChannelCode = this.config.apiServer.distributionChannelCode;
    body.domainCode = this.config.apiServer.domainCode;
    body.action = this.config.apiServer.action;
    body.cultureCode = this.language.getCurrentCultureCode()
    // body.platformCode = this.config.apiServer.platformCode;
    body.platformCode = 'WBE';

    return this.http.post<SearchAvailbilityRS>(`BTRBooking/ReadApartmentRoomRateAvailability`, body);
  }

  createSearchAvailabilityQueryString(params: SearchAvailabilityQuery): string{
    const result: any = {
      property: params.property,
      leaseLength: params.leaseLength || this.getDefaultLeaseLength(),
      fromDate: params.fromDate ? format_date(params.fromDate, 'YYYY-MM-DD') : '',
      bathRooms: params.bathRooms,
      bedRooms: params.bedRooms,
      furnished: params.furnished,
      priceRange: params.priceRange,
      roomTypeCode: params.roomTypeCode,
      roomNumber: params.roomNumber,
      ratePlanCode: params.ratePlanCode,
      applicationID: params.applicationID,
      promotionCode: params.promotionCode,
      discountsCode: params.discountCodes
    };

    Object.keys(result).forEach(key => {
      key= key.toString();
      if (result[key] === null || result[key] === undefined || result[key] === '') {
        delete result[key];
      }
    });
		const queryParams = new HttpParams({fromObject: result as any});
		return queryParams.toString();
  }

  parseSearchAvailabitity(urlParams: Record<SearchAvailabilityQueryKeys, any>, firstAvailableDate?: Date): SearchAvailabilityQuery | null{
    if(!urlParams || !urlParams['redirectAction']) {
      if(!urlParams || !urlParams['property']) return null;
      if(!urlParams['fromDate']) return null;
    }
    
    let fromDateKey = urlParams['fromDate'].toLowerCase();
    let isModifyFromDate = false;
    let fromDate: Date;

    if (fromDateKey === 'today' && firstAvailableDate) {
      fromDate = utc_date(new Date());
    } else if (fromDateKey === 'availdate' && firstAvailableDate) {
      fromDate = firstAvailableDate;
    } else {
      fromDate = local_date(urlParams['fromDate']);
    }

    if (firstAvailableDate && fromDate < firstAvailableDate) {
      fromDate = firstAvailableDate;
      isModifyFromDate = true;
    }

    const result: SearchAvailabilityQuery = {
      property: urlParams['property'],
      leaseLength: 12,
      fromDate: fromDate,
      bathRooms: 0,
      bedRooms: 0,
      isHasPet: false,
      isNeedExtraStorage: false,
      isNeedParking: false,
      whoMovingIn: urlParams['whoMoveIn']
    };

    if(urlParams['leaseLength']){
      result.leaseLength = Number(urlParams['leaseLength']);
    }

    if(urlParams['toDate']){
      const toDate = local_date(urlParams['toDate']);
      result.toDate = toDate;
    }

    if(urlParams['bathRooms']){
      result.bathRooms = urlParams['bathRooms'];
    }

    if(urlParams['bedRooms'] !== undefined){
      result.bedRooms = urlParams['bedRooms'];
    }

    if(urlParams['people']){
      result.people = parseInt(urlParams['people']);
    }

    if(urlParams['applicantType']){
      result.applicantType = urlParams['applicantType'];
    }

    if(urlParams['parking'] === 'true'){
      result.isNeedParking = true;
    }

    if(urlParams['storage'] === 'true'){
      result.isNeedExtraStorage = true;
    }

    if(urlParams['whoMoveIn']){
      result.whoMovingIn = urlParams['whoMoveIn'];
    }

    if(urlParams['furnished']){
      result.furnished = urlParams['furnished'];
    }

    if(urlParams['priceRange']){
      result.priceRange = urlParams['priceRange'];
    }

    if(urlParams['roomTypeCode']){
      result.roomTypeCode = urlParams['roomTypeCode'];
    }

    if(urlParams['roomNumber']){
      result.roomNumber = urlParams['roomNumber'];
    }

    if(urlParams['ratePlanCode']){
      result.ratePlanCode = urlParams['ratePlanCode'];
    }

    if(urlParams['applicationID']){
      result.applicationID = urlParams['applicationID'];
    }

    if(urlParams['promotionCode']){
      result.promotionCode = urlParams['promotionCode'];
    }

    if(urlParams['discountsCode']){
      result.discountCodes = urlParams['discountsCode'];
    }

    if (urlParams['leaseLength']) {
      const leaseLength = parseInt(urlParams['leaseLength'])
      result.leaseLength = leaseLength && leaseLength > 0 ? leaseLength : this.getDefaultLeaseLength();
    } else {
      result.leaseLength = this.getDefaultLeaseLength();
    }

    if (urlParams['redirectAction']) {
      result.redirectAction = urlParams['redirectAction'];
    }


    let isAtResultPage = this.router.url.includes('apartments/results');
    if ((['today', 'availdate',].includes(urlParams['fromDate'].toLowerCase()) || isModifyFromDate) && isAtResultPage && date_valid(fromDate)) {
      console.log(`isModifyFromDate: ${isModifyFromDate}`);
      const queryString = this.createSearchAvailabilityQueryString(result);
      this.router.navigateByUrl(`/apartments/results?${queryString}`);
    }
    
    return result;
  }

  getWhoMovingInData(): { value: string, translate?: string }[] {
    return [{
      translate: 'Just Me',
      value: 'JustMe'
    }, {
      translate: 'Housemate/s and me',
      value: 'HousematesAndMe'
    }, {
      translate: 'My partner and me',
      value: 'MyPartnerAndMe'
    }, {
      translate: 'My kids and me',
      value: 'MyKidsAndMe'
    }, {
      translate: 'My partner, kids and me',
      value: 'MyPartnerKidsAndMe'
    }]
  }

  saveDraftApplication(body: SaveDraftApplicationRQ): Observable<SaveDraftApplicationRS>{
    return this.http.post<SaveDraftApplicationRS>(`BTRBooking/SaveDraftApplication`, body, {
      context: new HttpContext().set(IS_SKIP_HANDLE_ERROR, true)
    });
  }

  readApplicationConfigurations(): Observable<ReadApplicationConfigurationsRS>{
    return this.http.post<ReadApplicationConfigurationsRS>(`BTRBooking/ReadApplicationConfigurations`, {});
  }

  readBTRAmenitiesSearchable(body: {distributionChannelCode: string}): Observable<ReadBTRAmenitiesSearchableRS>{
    return this.http.post<ReadBTRAmenitiesSearchableRS>(`BTRBooking/ReadBTRAmenitiesSearchable`, body);
  }

  readBTRHotelRoomAmenities(body: { propertyCode: string, roomTypeCode: string, roomNumber: string, distributionChannelCode: string }): Observable<ReadBTRHotelRoomAmenitiesRS> {
    return this.http.post<ReadBTRHotelRoomAmenitiesRS>(`BTRBooking/ReadBTRHotelRoomAmenities`, body);
  }

  readNoOfBedroom(): Observable<any>{
    return this.http.get<any>('BTRBooking/ReadNoOfBedRoom')
  }

  readLeaseLength(): Observable<any>{
    return this.http.get<any>('BTRBooking/ReadLeaseLength')
  }

  saveBookingInspection(body: any): Observable<ApiResponse>{
    return this.http.post<ApiResponse>(`BTRBooking/SaveRentalItemBookingSiteTour`, body);
  }

  readApplicationPaymentSummary(body: ReadApplicationPaymentSummaryRQ): Observable<ReadApplicationPaymentSummaryRS>{
    return this.http.post<ReadApplicationPaymentSummaryRS>(`BTRBooking/ReadApplicationPaymentSummary`, body);
  }

  saveSelectedApplicationInfo(data: SelectedApplicationInfo | SelectedSiteTourInfo){
    this.storage.setItem(SELETED_RATE_PLAN, data);
  }

  getSelectedApplicationInfo(): SelectedApplicationInfo | null {
    const data = this.storage.getItem(SELETED_RATE_PLAN) as SelectedApplicationInfo;
    if(data && data.type == 'booking'){
      return data;
    }
    return null;
  }

  getSelectedSiteTourInfo(): SelectedSiteTourInfo | null {
    const data = this.storage.getItem(SELETED_RATE_PLAN) as SelectedSiteTourInfo;
    if(data && data.type == 'site_tour'){
      return data;
    }
    return null;
  }

  clearSelectedApplicationInfo(){
    this.storage.removeItem(SELETED_RATE_PLAN);
    const data = this.storage.getItem(SELETED_RATE_PLAN)
  }

  readAllApplicationForProfile(): Observable<ReadAllApplicationForProfileRS>{
    return this.http.get<ReadAllApplicationForProfileRS>(`BTRResident/ReadAllApplicationForProfile`);
  }

  readRentalItemTypeRateAvailability(body: ReadRentalItemTypeRateAvailabilityRQ): Observable<ReadRentalItemTypeRateAvailabilityRS>{
    return this.http.post<any>(`BTRBooking/ReadRentalItemTypeRateAvailability`, body);
  }

  readProfileApplicationDetail(applicationID: string | number): Observable<BtrReadProfileApplicationDetailResponse> {
    const body: ReadProfileApplicationRQ = {
      applicationID: applicationID,
      action: 'WBEBooking',
      cultureCode: this.language.getCurrentCultureCode(),
      distributionChannelCode: this.config.apiServer.distributionChannelCode,
      domainCode: this.config.apiServer.distributionChannelCode,
      platformCode: 'WBE'
    };
    
    return this.http.post<BtrReadProfileApplicationDetailResponse>(`BTRBooking/ReadProfileApplication`, body);
  }

  getProfileContact(): Observable<BtrReadProfileResponse> {
    return this.http.get<BtrReadProfileResponse>(`BTRResident/ReadProfileContactDetail`);
  }

  getSiteTourTypeData(): {value: string, translate?: string}[]{
    return [{
      value: 'OnSite',
      translate: 'OnSite',
    }, {
      value: 'Virtual',
      translate: 'Virtual'
    }]
  }

  getFurnishedData(): { value: string, translate?: string }[] {
    return [{
      value: FurnishedEnum[FurnishedEnum.Furnished],
      translate: 'Yes',
    }, {
      value: FurnishedEnum[FurnishedEnum.Unfurnished],
      translate: 'No'
    },
    {
      value: FurnishedEnum[FurnishedEnum.Either],
      translate: 'Either'
    }
    ]
  }

  getStayWithData(): {value: string, translate?: string}[]{
    return [{
      value: `WeAreFamily`,
      translate: 'Me, my partner and kid(s)​',
    }, {
      value: `ItsJustMe`,
      translate: `Just me​`
    }, {
      value: 'WereFriends',
      translate: 'Sharing with friends​'
    }, {
      value: 'WereACouple',
      translate: 'Me and my partner​'
    }, {
      value: `Other`,
      translate: 'Me and my kid(s)​',
    }, 
    // {
    //   value: 'Other',
    //   translate: 'Other'
    // }
    ]
  }

  getMinBedRoomData(): {value: string, translate?: string}[]{
    return [{
      value: '0',
      translate: 'Studio',
    }, {
      value: '1',
      translate: '1'
    }, {
      value: '2',
      translate: '2'
    }, {
      value: '3',
      translate: '3'
    }, {
      value: 'any',
      translate: 'Any'
    }]
  }

  readAddsOnList(body: ReadAddsOnListRQ): Observable<ReadAddsOnListRS>{
    return this.http.post<ReadAddsOnListRS>(`BTRBooking/ReadAddsOnList`, body);
  }

  readSystemTypesByActivityType(propertyCode: string): Observable<ReadSystemTypesByActivityTypeRS>{
    const body: ReadSystemTypesByActivityTypeRQ = {
      distributionChannelCode: this.config.apiServer.distributionChannelCode,
      propertyCode,
      activityItemType: 'SiteTour'
    };
    
    return this.http.post<ReadSystemTypesByActivityTypeRS>(`BTRBooking/ReadSystemTypesByActivityType`, body);
  }

  checkRoomNumberAndRentalItemTypeAvailability(body: SaveDraftApplicationRQ): Observable<SaveDraftApplicationRS>{
    return this.http.post<SaveDraftApplicationRS>(`BTRBooking/CheckRoomNumberAndRentalItemTypeAvailability`, body, {
      context: new HttpContext().set(IS_SKIP_HANDLE_ERROR, true)
    });
  }

  readSearchDiscountByAccessCodeDistributionChannelCode(body: ReadSearchDiscountByAccessCodeDistributionChannelCodeRQ): Observable<ReadSearchDiscountByAccessCodeDistributionChannelCodeRS>{
    return this.http.post<ReadSearchDiscountByAccessCodeDistributionChannelCodeRS>(`BTRBooking/ReadSearchDiscountByAccessCodeDistributionChannelCode`, body);
  }

  calculateEndDate(startDate: Date, config: CountryGroupItem | undefined | null, totalMonths: number): Date{
    if(typeof startDate === 'string'){
      startDate = local_date(startDate);
    }
    if(!totalMonths){
      totalMonths = this.getDefaultLeaseLength();
    }

    if(!config){
      this.log.log(`calculateEndDate config is null.`);
    }

    if(config && config.isOpenEndedContract){
      return this.calculateOpenEndedContractEndDate(startDate, config.totalMonthsOpenEndedContract);
    }

    if(startDate.getDate() === 1){
      const endDate = new Date(startDate);
      endDate.setMonth(endDate.getMonth() + totalMonths);
      endDate.setDate(endDate.getDate() - 1)
      return endDate;
    }

    if(config && config.alwayFullMonth){
      return this.getLastDateOfNextMonth(startDate, totalMonths);
    }

    const result = new Date(startDate);

    result.setMonth(result.getMonth() + totalMonths);

    if(startDate.getDate() !== result.getDate()){
      // over next month, select the last date of the month
      result.setDate(1);
      result.setDate(result.getDate() - 1);
    }

    //config to decrease 1 day for end date
    if(Number(config?.rbeBillingSchedulesPattern) && !config?.alwayFullMonth) {
      result.setDate(result.getDate() - 1);
    }

    return result;
  }

  getLastDateOfNextMonth(date: Date, numberMonth: number): Date{
    const result = new Date(date);
    result.setDate(1);
    result.setMonth(result.getMonth() + numberMonth + 1);
    result.setDate(result.getDate() - 1);
    return result;
  }

  calculateOpenEndedContractEndDate(startDate: Date, numberMonth: number): Date{
    const result = new Date(startDate);
    result.setDate(1);
    result.setMonth(result.getMonth() + numberMonth + 1);
    result.setDate(result.getDate() - 1);

    this.log.log(`calculateOpenEndedContractEndDate start=${startDate.toJSON()}, months=${numberMonth}, end=${result.toJSON()}`);

    return result; 
  }

  getDefaultLeaseLength(): number{
    return 12;
  }

  redirectApplicationDetail(applicationID: string, code?: string){
    let path = '';
    if(applicationID && code){
      path = `activate-application/${code}/${applicationID}`;
      window.location.href = `${this.config.residentPortalSiteURL}/${path}`;
    }
    else{
      path = `apply-application/${applicationID}/pre-apply-application-checklist`;
      this.auth.redirectToRSP(path);
    }
  }

  getInProgressApplications(): Observable<ApiGetInProgressApplicationsRS>{
    return this.http.post<ApiGetInProgressApplicationsRS>(`BTRBooking/GetInProgressApplications`, {});
  }

  getMinimumLeaseEndDateConfig(propertyCode?: string): Date | null{
    if(propertyCode){
      const config = this.config.propertyMinimumLeaseStartDate?.find(i => i.propertyCode === propertyCode);
      if(config){
        const date = local_date(config.date);
        if(date_valid(date)){
          return date;
        }
      }
    }

    if(this.config.MinimumLeaseStartDate){
      const date = local_date(this.config.MinimumLeaseStartDate);
      if(date_valid(date)){
        return date;
      }
    }
    return null;
  }

  private _currentSearchFilter: CurrentFillter = {};
  setPriceRangeFilter(value: string){
    this._currentSearchFilter.priceRange = value;
  }

  setAmenityFilter(filters: AmenityItem[]) {
    this._currentSearchFilter.amenityFilter = filters.map(i => ({ amenityCode: i.amenityCode, category: i.category, amenityGroupCode: i.amenityGroupCode || '' }));
  }

  hasPriceRangeFilter(): boolean{
    if(!this._currentSearchFilter){
      return false;
    }

    return this._currentSearchFilter.priceRange ? true : false;
  }

  getPriceRangeFilter(): string{
    return this._currentSearchFilter.priceRange || '';
  }

  getAmenityFilter() {
    return this._currentSearchFilter.amenityFilter || [];
  }

  downloadFile(file: any, fileName: string, contentType: string = 'application/pdf', isDownload = true){
    const browser = this.getBrowserName();
  
    if(browser === 'safari'){
      const newBlob = new Blob([file], { type: contentType });

      const data = window.URL.createObjectURL(newBlob);

      const link = document.createElement('a');
      link.href = data;

      if(isDownload){
        link.download = `${fileName}.pdf`;
      }
      else{
        link.target = '_blank';
      }
      link.click();
    }
    else{
      this.saveFileInBrowser(file, fileName, contentType, isDownload);
    }  
  }

  saveFileInBrowser(res: any, fileName: string, contentType?: string, isDownload = true) {
    if (!res) {
      return;
    }

    if(!contentType){
      contentType = 'application/pdf';
    }

    // create new blob with mime type for chrome
    const newBlob = new Blob([res], { type: contentType });

    // for IE
    if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
      (window.navigator as any).msSaveOrOpenBlob(newBlob);
      return;
    }

    // For other browsers
    // Create a link pointing to the ObjectURL containing the blob.
    const data = window.URL.createObjectURL(newBlob);

    const link = document.createElement('a');
    link.href = data;
    if(isDownload){
      link.download = fileName; // fullFileName
    }
    else{
      link.target = '_blank';
    }
    // this is necessary as link.click() does not work on the latest firefox
    link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));

    setTimeout(() => {
      // For Firefox it is necessary to delay revoking the ObjectURL
      // window.URL.revokeObjectURL(data);
      link.remove();
    }, 100);
  }

  getBrowserName() {
    const agent = window.navigator.userAgent.toLowerCase()
    switch (true) {
      case agent.indexOf('edge') > -1:
        return 'edge';
      case agent.indexOf('opr') > -1 && !!(<any>window).opr:
        return 'opera';
      case agent.indexOf('chrome') > -1 && !!(<any>window).chrome:
        return 'chrome';
      case agent.indexOf('trident') > -1:
        return 'ie';
      case agent.indexOf('firefox') > -1:
        return 'firefox';
      case agent.indexOf('safari') > -1:
        return 'safari';
      default:
        return 'other';
    }
  }

  getCookie(name: string): string | null {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  getUtmByName(utm_param: string) {
    return this.getCookie(`${utm_param}y`);
  }

  getUtmFromCookies(): UtmData {
    return {
      source: this.getUtmByName('source') || undefined,
      medium: this.getUtmByName('medium') || undefined,
      campaign: this.getUtmByName('campaign') || undefined,
      content: this.getUtmByName('content') || undefined,
      term: this.getUtmByName('term') || undefined,
    }
  }

  getUtmContent(action: 'siteTour' | 'register'): UtmData | null {
    const data = this.storage.getItem(UTM_SOURCE_DATA)
    const utmLocal: UtmDataSaveLocal = data ? data : null;
    const cookieUtm: UtmData = this.getUtmFromCookies() 

    if(!utmLocal) {
      return cookieUtm
    } else {
      if(!this.compareUtmData(cookieUtm, utmLocal)) {
        return cookieUtm;
      } else {
        if((action == 'siteTour' && utmLocal.sendSiteTour) || (action == 'register' && utmLocal.sendRegister)){
          return null;
        } else {
          return cookieUtm;
        }
      }
    }
  }

  compareUtmData(newUtm: UtmData, oldUtm: UtmData) {
    return newUtm.source == oldUtm.source
        && newUtm.medium == oldUtm.medium
        && newUtm.content == oldUtm.content
        && newUtm.campaign == oldUtm.campaign
        && newUtm.term == oldUtm.term
  }

  saveSendUtmLocal(action: 'siteTour' | 'register') {
    const utmData: UtmData = this.getUtmFromCookies();
    const data = this.storage.getItem(UTM_SOURCE_DATA)
    const utmLocal = data ? data : null;
    if(!utmLocal) {
      const saveData: UtmDataSaveLocal = {
        ...utmData,
        sendSiteTour: action === 'siteTour' ? true : false,
        sendRegister: action === 'register' ? true : false,
      }
      this.storage.setItem(UTM_SOURCE_DATA, saveData)
    } else {
      let compare = this.compareUtmData(utmData, utmLocal)
      let data: UtmDataSaveLocal = compare ? { ...utmLocal }: { ...utmData };
      (action === 'siteTour') && (data.sendSiteTour = true);
      (action === 'register') && (data.sendRegister = true);
      this.storage.setItem(UTM_SOURCE_DATA, data)
    }
  }

  readHotelContactInformation(propertyCode: string): Observable<ApiReadHotelContactInformationRS>{
    return this.http.post<ApiReadHotelContactInformationRS>('BTRBooking/ReadHotelContactInformation', {propertyCode: propertyCode});
  }
}

export interface CurrentFillter{
  priceRange?: string,
  amenityFilter?: { amenityCode: string, category: string, amenityGroupCode: string }[]
}

export interface UtmData {
  source: string | undefined,
  medium: string | undefined,
  campaign: string | undefined,
  term: string | undefined,
  content: string | undefined,
}

export interface UtmDataSaveLocal extends UtmData {
  sendSiteTour: boolean,
  sendRegister: boolean,
}

export enum FurnishedEnum {
  Furnished,
  Unfurnished,
  Either
}